import axios from '@/libs/axiosInstance';

export interface InstagramPage {
  id: string;
  name: string;
  accessToken: string;
  isPageToken?: boolean;
  instagramBusinessAccountId?: string;
}

/**
 * Response from the OAuth redirect endpoint containing PKCE parameters
 */
export interface OAuthRedirectResponse {
  redirectUrl: string;
  codeVerifier: string;
  state: string;
}

/**
 * Request payload for exchanging authorization code for tokens
 */
export interface CodeExchangeRequest {
  code: string;
  codeVerifier: string;
  state: string;
  brandId: string;
}

/**
 * Response from code exchange endpoint
 */
export interface CodeExchangeResponse {
  pages: InstagramPage[];
  tokenInfo: {
    expiresIn: number;
    expiresInDays: number;
    hasRefreshToken: boolean;
    hasIdToken: boolean;
    scope?: string;
  };
  idTokenClaims?: any;
}

/**
 * Get OAuth redirect URL with PKCE parameters
 * @returns OAuth URL and PKCE parameters for the authorization flow
 */
export const getOAuthRedirect = async (): Promise<OAuthRedirectResponse> => {
  const { data } = await axios.get('/dashboard/integrations/fb/redirect-url');
  return data;
};

/**
 * Exchange authorization code for access tokens using PKCE
 * @param payload Authorization code and PKCE parameters
 * @returns Access token and page information
 */
export const exchangeAuthorizationCode = async (
  payload: CodeExchangeRequest
): Promise<CodeExchangeResponse> => {
  const { data } = await axios.post('/dashboard/integrations/fb/exchange-code', payload);
  return data;
};

/**
 * Finalize Instagram connection with selected page
 * @param page Selected Instagram page with all details
 * @param brandId Brand ID to associate the connection with
 */
export const finalizeInstagramConnection = async (page: InstagramPage, brandId: string) => {
  const { data } = await axios.post('/dashboard/integrations/fb/finalize', {
    pageId: page.id,
    brandId,
    accessToken: page.accessToken,
    isPageToken: page.isPageToken || false,
    instagramBusinessAccountId: page.instagramBusinessAccountId,
    pageName: page.name,
    instagramUsername: page.name, // Using page name as username fallback
  });
  return data;
};
